import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {AbstractControl, UntypedFormControl} from '@angular/forms';
import {Subject} from 'rxjs';
import {concatMap, take, takeUntil, tap} from 'rxjs/operators';
import {ZipCode} from '../../models/common';
import {AddressService} from '../../services';
import * as Sentry from "@sentry/angular-ivy";

@Component({
  selector: 'app-address',
  templateUrl: './address.component.html',
  styleUrls: ['./address.component.scss'],
})
export class AddressComponent implements OnInit, OnDestroy, OnChanges {
  @Input() public label ? = '';
  @Input() public clientAddress?: string;
  @Input() public clientZipCode?: ZipCode;
  @Input() public isSameAsClientControl: UntypedFormControl = new UntypedFormControl();
  @Input() public addressControl: UntypedFormControl = new UntypedFormControl();
  @Input() public zipCodeControl: UntypedFormControl = new UntypedFormControl();
  @Input() public zipCodeExtraControl: UntypedFormControl = new UntypedFormControl();
  @Input() public required = false;
  @Input() public warningIconForRequired ? = false;
  @Input() public loadInformation = new EventEmitter();
  @Input() public validationGroup: string = '';
  @Input() public disabled = false;
  public city?: string = '';
  public state?: string = '';
  public onDestroy$: Subject<void> = new Subject<void>();
  public isLoading = false;
  public isLoadingZipCodeExtra = false;
  public zipCodeMaskRule = {X: /[0-9]/};

  constructor(public addressService: AddressService, private ref: ChangeDetectorRef) {
    this.calculateAddress = this.calculateAddress.bind(this);
  }

  public ngOnInit() {
    this.zipCodeExtraControl?.setValue('');
    if (this.isSameAsClientControl) {
      this.isSameAsClientControl.valueChanges
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(value => {
          if (value) {
            this.setSameAsClientAddress();
          } else {
            this.clean();
          }
        });
    }

    this.loadInformation
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(res => {
        if (res) {
          this.calculateAddress();
        }
      });
  }

  private setDataControl() {
    const isSameAsClient = this.isSameAsClientControl ? this.isSameAsClientControl.value : false;
    this.addressControl?.setValue(isSameAsClient ? this.clientAddress : this.addressControl.value);
    this.zipCodeControl?.setValue(isSameAsClient ? this.clientZipCode?.code : this.zipCodeControl.value);
    this.calculateAddress();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.clientAddress || changes.clientZipCode) {
      if (this.isSameAsClientControl?.value) {
        this.setSameAsClientAddress();
      }
    }
  }

  private setSameAsClientAddress() {
    const isSameAsClient = this.isSameAsClientControl ? this.isSameAsClientControl.value : false;
    if (isSameAsClient) {
      this.setDataControl();
    } else {
      this.clean();
    }
  }

  public clean() {
    this.addressControl?.setValue(null);
    this.zipCodeControl?.setValue(null);
    this.city = '';
    this.state = '';
    this.zipCodeExtraControl?.setValue('');
  }

  public calculateAddress() {
    if (!this.zipCodeControl?.value || !this.addressControl?.value) {
      return;
    }
    this.addressService.getZipCodeByCode(this.zipCodeControl.value)
      .pipe(
        take(1),
        tap(zipCode => {
          this.city = zipCode.city;
          this.state = zipCode.state;
          this.zipCodeControl?.setValue(zipCode.code);
        }),
        concatMap(zipCode => this.addressService.calculateZipCodePlus4(zipCode.id, this.addressControl?.value)))
      .subscribe(zipCodePlus4 => {
        this.zipCodeExtraControl?.setValue(zipCodePlus4);
        this.ref.detectChanges();
      }, (error => Sentry.captureException(error)));
  }

  public get IsDisabled() {
    return this.clientAddress === null || this.clientZipCode == null;
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
